import { useState } from 'react';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import AddSubscriptionModal from './AddSubscriptionModal';
import DedicatedIpStatus from './DedicatedIpStatus';
import DedicatedIpModal from './DedicatedIpModal';
import UpgradePlanAction from './UpgradePlanAction';
import UpgradePlanModal from './UpgradePlanModal';
import IntervalSwitchModal from './IntervalSwitchModal';
import { usePortalSession, useReactivateSubscription, useUpgradeOptions } from '../../hooks/useBilling';
import { upgradeDeepLinkNotice, useUpgradeDeepLink } from '../../hooks/useUpgradeDeepLink';
import type { BillingStatus, Subscription, UpgradeOption } from '../../types';

interface Props {
  billing: BillingStatus;
  subscriptions?: Subscription[];
}

export default function StripeCard({ billing, subscriptions }: Props) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [dipModalSub, setDipModalSub] = useState<{ id: string; hostname: string } | null>(null);
  const [upgradeOption, setUpgradeOption] = useState<UpgradeOption | null>(null);
  const [intervalSwitchOption, setIntervalSwitchOption] = useState<UpgradeOption | null>(null);
  const portalSession = usePortalSession();
  const reactivate = useReactivateSubscription();
  const { data: upgradeOptions } = useUpgradeOptions();
  const upgradeDeepLinkStatus = useUpgradeDeepLink(upgradeOptions, setUpgradeOption);
  const deepLinkNotice = upgradeDeepLinkNotice(upgradeDeepLinkStatus);

  const reactivated = new URLSearchParams(window.location.search).get('reactivated') === 'true';

  const suspendedSubs = subscriptions?.filter(
    (s) => s.status === 'suspended' || s.status === 'archived'
  ) ?? [];

  const subByHostname = new Map(
    subscriptions?.map(s => [s.hostname, s]) ?? []
  );

  const upgradeOptionBySubId = new Map(
    upgradeOptions?.subscriptions.map(o => [o.subscription_id, o]) ?? []
  );

  const handleManageBilling = () => {
    portalSession.mutate();
  };

  return (
    <Card className="p-6">
      {reactivated && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
          <p className="text-sm text-green-800">
            Your service is being restored. You'll receive an email when it's ready.
          </p>
        </div>
      )}
      {deepLinkNotice && (
        <p className={`text-sm mb-4 ${deepLinkNotice.tone === 'error' ? 'text-red-700' : 'text-amber-700'}`}>
          {deepLinkNotice.message}
        </p>
      )}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Subscription Management
          </h2>
          <p className="text-gray-600 mb-4">
            Manage your subscription, update payment methods, and view invoices.
          </p>

          {billing.subscriptions && billing.subscriptions.length > 0 && (
            <div className="space-y-2 mb-4">
              {billing.subscriptions.map((sub) => {
                const upgradeOptionForSub = upgradeOptionBySubId.get(sub.subscription_id);
                return (
                  <div
                    key={sub.subscription_id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <span className="font-medium text-gray-900">
                      {sub.plan_name}
                    </span>
                    {sub.hostname && (
                      <span className="text-gray-500">
                        {sub.hostname}
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        sub.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {sub.status}
                    </span>
                    {(() => {
                      const fullSub = subByHostname.get(sub.hostname ?? '');
                      if (!fullSub) return null;
                      return (
                        <DedicatedIpStatus
                          eligible={fullSub.dedicated_ip_eligible ?? false}
                          status={fullSub.dedicated_ip_status ?? null}
                          ip={fullSub.dedicated_ip ?? null}
                          onAddClick={() => setDipModalSub({ id: fullSub.id, hostname: fullSub.hostname })}
                        />
                      );
                    })()}
                    {sub.status === 'active' && (
                      <UpgradePlanAction
                        option={upgradeOptionForSub}
                        onUpgradeClick={setUpgradeOption}
                      />
                    )}
                    {sub.status === 'active' && upgradeOptionForSub?.interval_switch?.eligible && (
                      <button
                        onClick={() => setIntervalSwitchOption(upgradeOptionForSub)}
                        title="Switch to Annual Billing"
                        className="px-2 py-0.5 text-xs font-medium text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                      >
                        Switch to Annual
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          <svg
            className="w-12 h-12 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        </div>
      </div>

      <div className="flex gap-3 mt-4">
        <button
          onClick={handleManageBilling}
          disabled={portalSession.isPending}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors flex items-center justify-center gap-2"
        >
          {portalSession.isPending ? (
            <>
              <Spinner size="sm" className="border-white border-t-transparent" />
              <span>Opening...</span>
            </>
          ) : (
            'Manage Billing'
          )}
        </button>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={portalSession.isPending}
          className="flex-1 px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-50 font-medium transition-colors"
        >
          Add Subscription
        </button>
      </div>

      {portalSession.isError && (
        <p className="mt-2 text-sm text-red-600">
          Failed to open billing portal. Please try again.
        </p>
      )}

      {/* Suspended/archived subscriptions from subscriptions API */}
      {suspendedSubs.length > 0 && (
        <div className="space-y-3 mt-4 pt-4 border-t border-gray-200">
          {suspendedSubs.map((sub) => (
            <div
              key={sub.id}
              className="border border-amber-200 bg-amber-50 rounded-md p-3"
            >
              <div className="flex items-center gap-2 text-sm mb-1">
                <span className="font-medium text-gray-900">
                  {sub.plan || 'Unknown'}
                </span>
                <span className="text-gray-500">{sub.hostname}</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {sub.status === 'archived' ? 'Archived' : 'Suspended'}
                </span>
              </div>
              <p className="text-xs text-amber-800 mb-2">
                Your service has been suspended due to non-payment.
                A one-time restoration fee applies.
              </p>
              <button
                onClick={() => reactivate.mutate(sub.id)}
                disabled={reactivate.isPending}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 font-medium transition-colors"
              >
                {reactivate.isPending ? 'Redirecting...' : 'Reactivate Service'}
              </button>
            </div>
          ))}

          {reactivate.isError && (
            <p className="text-sm text-red-600">
              {reactivate.error instanceof Error ? reactivate.error.message : 'Failed to start reactivation'}
            </p>
          )}
        </div>
      )}

      <AddSubscriptionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      {dipModalSub && (
        <DedicatedIpModal
          isOpen
          onClose={() => setDipModalSub(null)}
          hostname={dipModalSub.hostname}
          subscriptionId={dipModalSub.id}
        />
      )}

      {upgradeOption && (
        <UpgradePlanModal
          isOpen
          onClose={() => setUpgradeOption(null)}
          option={upgradeOption}
        />
      )}

      {intervalSwitchOption && (
        <IntervalSwitchModal
          isOpen
          onClose={() => setIntervalSwitchOption(null)}
          option={intervalSwitchOption}
        />
      )}
    </Card>
  );
}
